/**
 * Customer.io (browser) exclusion — mirrors backend_service/service/customerio/guard.js.
 *
 * AppSumo users and sub-users (and team members of an AppSumo owner, who share
 * the owner's profile) must never be sent to Customer.io. The tracker snippet
 * in public/index.html and components/Layout/ThirdParty.js checks the same
 * localStorage flags before loading; once the profile is loaded,
 * applyCustomerIoExclusion() makes the decision final and stores `cio_off`.
 */

const CIO_METHODS = ['load', 'identify', 'sidentify', 'track', 'page'];

const WN_METHODS = ['page', 'identify', 'track', 'group', 'alias'];

/**
 * Replace window._cio (old workspace) with a stub and mute window.cioanalytics
 * (new WN workspace snippet, loaded from GTM) so neither sends anything.
 */
export const disableCustomerIo = () => {
  const stub = [];
  CIO_METHODS.forEach((m) => { stub[m] = () => { }; });
  window._cio = stub;
  const wn = window.cioanalytics;
  if (wn) {
    WN_METHODS.forEach((m) => { try { wn[m] = () => wn; } catch (e) { /* read-only */ } });
  } else {
    // pre-set `initialize` so the GTM snippet skips loading at all
    window.cioanalytics = { initialize: true, invoked: true };
    WN_METHODS.forEach((m) => { window.cioanalytics[m] = () => window.cioanalytics; });
  }
};

export const isCustomerIoExcluded = (profile) => !!profile && (
  profile.utyp === 'appsumo' ||
  profile.iapftk === true ||
  (Array.isArray(profile.apstk) && profile.apstk.length > 0) ||
  profile.isbusr === true
);

/** Call after the profile is fetched. Returns true when tracking is disabled. */
export const applyCustomerIoExclusion = (profile) => {
  const excluded = isCustomerIoExcluded(profile);
  try { localStorage.setItem('cio_off', excluded ? 'true' : 'false'); } catch (e) { /* storage unavailable */ }
  if (excluded) disableCustomerIo();
  return excluded;
};

/**
 * Tell the WN workspace snippet (window.cioanalytics, from GTM) who this visitor is.
 * Same id as the backend (ti), so Customer.io merges the visitor's earlier anonymous
 * page views (blog, pricing, ...) into the person -> "which pages led to signup".
 * Only identity traits here; the backend owns every other attribute.
 * Works before the snippet has loaded: the call is queued and replayed on load.
 */
export const identifyCustomerIoWN = (profile) => {
  if (!profile || !profile.ti || isCustomerIoExcluded(profile)) return;
  const email = profile.orge || profile.e;
  if (!email) return;
  const traits = { email };
  if (profile.fn) traits.name = profile.fn;
  try {
    const wn = (window.cioanalytics = window.cioanalytics || []);
    if (typeof wn.identify === 'function') wn.identify(profile.ti, traits);
    else wn.push(['identify', profile.ti, traits]);
  } catch (e) { /* tracker unavailable */ }
};
